import { useEffect } from 'react';
import { isRouteErrorResponse, useNavigate, useRevalidator, useRouteError } from 'react-router';

import { getTraceId } from '@shared/api';
import { logger } from '@shared/lib/logger';
import { errorReporter } from '@shared/lib/observability';
import { StatusScreen } from '@shared/ui/status-screen';

import { ERROR_BOUNDARY_TEXT } from './error-boundary.text';
import { ErrorFallback } from './error-fallback.ui';

/**
 * 라우트의 `ErrorBoundary` 자리에 넣는다. 그 라우트의 lazy 로드 · loader · 렌더 오류를 받는다.
 * 페이지 하나의 오류가 앱 전체(탭 줄 · 레이아웃)를 내리지 않게 한다.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (notFound) return;
    const traceId = getTraceId(error);
    const context = { boundary: 'route', ...(traceId ? { traceId } : {}) };
    logger.error(ERROR_BOUNDARY_TEXT.logRoute, { ...context, error });
    errorReporter.capture(error, context);
  }, [error, notFound]);

  if (notFound) {
    return (
      <StatusScreen
        inline
        title={ERROR_BOUNDARY_TEXT.notFoundTitle}
        description={ERROR_BOUNDARY_TEXT.notFoundDescription}
        action={{ label: ERROR_BOUNDARY_TEXT.goHome, onClick: () => void navigate('/') }}
      />
    );
  }

  return (
    <ErrorFallback
      inline
      error={error}
      reset={() => {
        void revalidator.revalidate();
        void navigate(0);
      }}
    />
  );
}
